import React, { useEffect, useState } from 'react';
import { CalendarDays, Hash, History, MessageSquareText, Package, UserRound, UserRoundX } from 'lucide-react';
import AdminPagination from '../components/AdminPagination';
import { useTranslations } from '../translations';
import { AdminReportedChat, fetchReportedChats } from '../services/admin.api';
import './AdminReportedChats.css';

interface AdminReportedChatsProps {
  onNavigateToProductDetail: (id: string) => void;
}

const AdminReportedChats: React.FC<AdminReportedChatsProps> = ({ onNavigateToProductDetail }) => {
  const t = useTranslations();
  const [reportedChats, setReportedChats] = useState<AdminReportedChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [productNameFilter, setProductNameFilter] = useState('');
  const [reportedByFilter, setReportedByFilter] = useState('');
  const [reportedUserFilter, setReportedUserFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    productName: '',
    reportedBy: '',
    reportedUser: '',
  });

  useEffect(() => {
    loadReportedChats();
  }, [currentPage, appliedFilters]);

  const loadReportedChats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchReportedChats({
        page: currentPage,
        size: 10,
        ...appliedFilters,
      });
      setReportedChats(response.content || []);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      console.error('Error loading reported chats:', err);
      setError(t.administration.reportedChats.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (user: AdminReportedChat['reportedBy']) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id;
  };

  const handleSearch = () => {
    const nextFilters = {
      productName: productNameFilter.trim(),
      reportedBy: reportedByFilter.trim(),
      reportedUser: reportedUserFilter.trim(),
    };

    setAppliedFilters(nextFilters);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      productName: '',
      reportedBy: '',
      reportedUser: '',
    };

    setProductNameFilter('');
    setReportedByFilter('');
    setReportedUserFilter('');
    setAppliedFilters(emptyFilters);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const formatDate = (value: string) => new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

  return (
    <div className="admin-reported-chats">
      <div className="admin-reported-chats-filters">
        <div className="admin-reported-chats-search">
          <div className="admin-reported-chats-search-field">
            <Package className="admin-reported-chats-search-icon" aria-hidden />
            <input
              type="text"
              placeholder={t.administration.reportedChats.productNameFilter}
              aria-label={t.administration.reportedChats.productNameFilter}
              value={productNameFilter}
              onChange={(e) => setProductNameFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-reported-chats-search-input"
            />
          </div>

          <div className="admin-reported-chats-search-field">
            <UserRound className="admin-reported-chats-search-icon" aria-hidden />
            <input
              type="text"
              placeholder={t.administration.reportedChats.reportedByFilter}
              aria-label={t.administration.reportedChats.reportedByFilter}
              value={reportedByFilter}
              onChange={(e) => setReportedByFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-reported-chats-search-input"
            />
          </div>

          <div className="admin-reported-chats-search-field">
            <UserRoundX className="admin-reported-chats-search-icon" aria-hidden />
            <input
              type="text"
              placeholder={t.administration.reportedChats.reportedUserFilter}
              aria-label={t.administration.reportedChats.reportedUserFilter}
              value={reportedUserFilter}
              onChange={(e) => setReportedUserFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="admin-reported-chats-search-input"
            />
          </div>

          <div className="admin-reported-chats-search-actions">
            <button type="button" onClick={handleSearch} className="admin-reported-chats-search-btn">
              {t.common.search}
            </button>
            <button type="button" onClick={handleResetFilters} className="admin-reported-chats-reset-btn">
              {t.searchResults.resetFilters}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="admin-reported-chats-loading">{t.common.loading}</div>
      ) : error ? (
        <div className="admin-reported-chats-error">{error}</div>
      ) : reportedChats.length === 0 ? (
        <div className="admin-reported-chats-empty">{t.administration.reportedChats.noChats}</div>
      ) : (
        <div className="admin-reported-chats-list">
          {reportedChats.map((reportedChat) => (
            <div className="admin-reported-chat-card" key={reportedChat.id}>
              <div className="admin-reported-chat-user">
                <div className="admin-reported-chat-label">{t.administration.reportedChats.reportedBy}</div>
                <div className="admin-reported-chat-name">{getUserName(reportedChat.reportedBy)}</div>
                <div className="admin-reported-chat-email">{reportedChat.reportedBy.email}</div>
              </div>

              <div className="admin-reported-chat-user">
                <div className="admin-reported-chat-label">{t.administration.reportedChats.reportedUser}</div>
                <div className="admin-reported-chat-name">{getUserName(reportedChat.reportedUser)}</div>
                <div className="admin-reported-chat-email">{reportedChat.reportedUser.email}</div>
              </div>

              <div className="admin-reported-chat-product">
                <div className="admin-reported-chat-product-image">
                  {reportedChat.product.image?.imageUrl ? (
                    <img src={reportedChat.product.image.thumbnailUrl || reportedChat.product.image.imageUrl} alt={reportedChat.product.title} />
                  ) : (
                    <span>{t.adminCard.noImage}</span>
                  )}
                </div>
                <div className="admin-reported-chat-product-content">
                  <div className="admin-reported-chat-label">{t.administration.reportedChats.product}</div>
                  <button
                    type="button"
                    className="admin-reported-chat-product-title"
                    onClick={() => onNavigateToProductDetail(reportedChat.product.id)}
                  >
                    {reportedChat.product.title}
                  </button>
                </div>
              </div>

              <div className="admin-reported-chat-reasons">
                <div className="admin-reported-chat-label">{t.administration.reportedChats.reasons}</div>
                <div className="admin-reported-chat-reasons-list">
                  {(reportedChat.reasons || []).map((reason) => (
                    <span className="admin-reported-chat-reason" key={reason}>{reason}</span>
                  ))}
                </div>
              </div>

              <div className="admin-reported-chat-meta">
                <span className="admin-reported-chat-id">
                  <Hash aria-hidden />
                  <span>
                    <small>{t.administration.reportedChats.conversationId}</small>
                    <code>{reportedChat.chatId}</code>
                  </span>
                </span>
                <span>
                  <CalendarDays aria-hidden />
                  <time dateTime={reportedChat.createdAt}>{formatDate(reportedChat.createdAt)}</time>
                </span>
                <span>
                  <MessageSquareText aria-hidden />
                  {reportedChat.messageCount} {t.administration.reportedChats.messages}
                </span>
                <span>
                  <History aria-hidden />
                  {reportedChat.previousReportsForUser} {t.administration.reportedChats.previousReports}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

    </div>
  );
};

export default AdminReportedChats;
